// CoachTeamProfilePage.jsx
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { CoachTeamHeader } from "../coachTeams/CoachTeamHeader";
import { RecentTeamStats } from "../coachTeams/RecentTeamStats";
import { PlayerTable } from "../coachTeams/PlayerTable";
import { CARD, CardTitle } from "../../dashboard/profile/profileCard";
import { TeamTabs, TeamSchedule, TeamStatistics, DEFAULT_STAT_COLUMNS } from "../coachTeams/TeamTabs";
import { SimpleDropdown } from '../../../components/common/SimpleDropdown';
import { fetchCoachTeamRoster, loadMoreCoachTeamRoster } from '../../../features/team/teamSlice';
import { CoachSidebar } from '../../../components/coach/dashboard/CoachSidebar';
import { currentSeasonYear, statsSeasonYear, seasonLabel } from '../../../utils/season';

export const CoachTeamProfilePage = () => {
  const { teamId } = useParams();
  const [activeTab, setActiveTab] = useState('roster');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { teamRoster, rosterPagination, rosterTeamInfo, loading, error } = useSelector((state) => state.team);

  // Filter state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterRanges, setFilterRanges] = useState({});
  const [rosterLimit, setRosterLimit] = useState(20);
  const [selectedStatColumns, setSelectedStatColumns] = useState([]);
  const [filterActiveTab, setFilterActiveTab] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  const [scope, setScope] = useState('overall');
  const [category, setCategory] = useState('');

  // Generate year list - current season and 10 previous years in season format (2026-27)
  const currentYear = currentSeasonYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => {
    const year = currentYear - i;
    return {
      label: seasonLabel(year),
      value: year
    };
  });
  // Roster tab shows the current season (or last season until the team posts
  // its new roster); Statistics shows the last season with games played.
  const [rosterYear, setRosterYear] = useState(currentYear);
  const selectedYear = activeTab === 'statistics' ? statsSeasonYear() : rosterYear;

  useEffect(() => {
    setRosterYear(currentSeasonYear());
  }, [teamId]);

  // Get team data from navigation state or first player's team
  const teamFromState = location.state?.teamData;
  const currentTeamData = rosterTeamInfo || teamFromState || teamRoster[0]?.team || {};

  const handleApplyFilters = (filters) => {
    if (filters.selectedFilters && filters.selectedFilters.length > 0) {
      setSelectedStatColumns(filters.selectedFilters);
      setFilterActiveTab(filters.activeTab);
    } else {
      setSelectedStatColumns([]);
      setFilterActiveTab(null);
    }

    if (filters.filterRanges) {
      setFilterRanges(filters.filterRanges);
    } else {
      setFilterRanges({});
    }
  };

  const resolveStatsType = (tab, cat) => {
    if (tab === 'pitching' || cat === 'pitching') return 'pitching';
    if (tab === 'hitting' || cat === 'hitting') return 'batting';
    return null;
  };

  // Filters only apply on the Statistics tab; the Roster tab is the plain list.
  const rosterQuery = useMemo(() => (
    activeTab === 'statistics'
      ? { statsType: resolveStatsType(filterActiveTab, category), scope, category, filters: filterRanges }
      : { statsType: null, scope: 'overall', category: '', filters: {} }
  ), [activeTab, filterActiveTab, category, scope, filterRanges]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default stat columns until the coach picks their own with the filters
  const statsTab = filterActiveTab || (category === 'pitching' ? 'pitching' : 'hitting');
  const statColumns = selectedStatColumns.length > 0 ? selectedStatColumns : DEFAULT_STAT_COLUMNS[statsTab];

  // Fetch team roster using team ID from route
  useEffect(() => {
    if (teamId) {
      const params = {
        teamId,
        page: 1,
        limit: rosterLimit,
        seasonYear: selectedYear,
        statsType: rosterQuery.statsType,
        scope: rosterQuery.scope,
        category: rosterQuery.category,
      };

      if (Object.keys(rosterQuery.filters).length > 0) {
        params.filters = rosterQuery.filters;
      }

      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }

      const request = dispatch(fetchCoachTeamRoster(params));

      // New season roster not posted yet: fall back to last season's
      if (activeTab === 'roster' && selectedYear === currentSeasonYear()) {
        request.then((action) => {
          const players = action.payload?.players || action.payload?.roster || [];
          if (fetchCoachTeamRoster.fulfilled.match(action) && players.length === 0) {
            setRosterYear(selectedYear - 1);
          }
        });
      }
    }
  }, [dispatch, teamId, selectedYear, filterRanges, rosterLimit, sortBy, sortOrder, rosterQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    if (teamId && rosterPagination?.hasMore && !loading) {
      const nextPage = (rosterPagination.currentPage || 1) + 1;
      const params = {
        teamId,
        page: nextPage,
        limit: rosterLimit,
        seasonYear: selectedYear,
        statsType: rosterQuery.statsType,
        scope: rosterQuery.scope,
        category: rosterQuery.category,
      };

      if (Object.keys(rosterQuery.filters).length > 0) {
        params.filters = rosterQuery.filters;
      }

      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }

      dispatch(loadMoreCoachTeamRoster(params));
    }
  };

  const handleSort = (backendField, direction) => {
    setSortBy(backendField);
    setSortOrder(direction);
  };

  const handleYearChange = (year) => {
    setRosterYear(parseInt(year));
  };

  // Client-side filtering by category (backend doesn't filter by position/category)
  const filteredRoster = useMemo(() => {
    if (!category) return teamRoster;
    const isPitcher = (p) => {
      const pos = (p.primaryPosition || p.position || '').toUpperCase();
      return pos === 'RHP' || pos === 'LHP' || pos === 'P' || pos === 'PITCHER';
    };
    if (category === 'pitching') return teamRoster.filter(isPitcher);
    if (category === 'hitting') return teamRoster.filter((p) => !isPitcher(p));
    return teamRoster;
  }, [teamRoster, category]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left Sidebar */}
      <div className="lg:col-span-1">
        <CoachSidebar activeView="teams" />
      </div>

      {/* Right Content Area */}
      <main className="lg:col-span-3 flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-jp-muted hover:text-jp-ink text-sm font-medium transition-colors mb-6"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Teams</span>
          </button>


          {/* Loading State */}
          {loading && teamRoster.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Team Content - Show when we have team data from state or API */}
          {(teamFromState || currentTeamData._id || !loading) && (
            <div className="space-y-6">
              {(teamFromState || currentTeamData._id) && (
                <CoachTeamHeader
                  selectedCollege={currentTeamData._id ? currentTeamData : teamFromState}
                  colleges={[currentTeamData._id ? currentTeamData : teamFromState]}
                  onSelectCollege={() => {}}
                />
              )}

              <TeamTabs value={activeTab} onChange={setActiveTab} />

              {activeTab === 'schedule' && <TeamSchedule />}
              {activeTab === 'roster' && (
              <section className={`${CARD} text-left`}>
                <CardTitle title={`Roster · ${seasonLabel(selectedYear)}`} />

                {/* Year Selector */}
                {/* <div className="flex items-center justify-end py-4 border-b border-jp-line">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-jp-ink">Season Year:</span>
                    <div style={{ minWidth: '140px' }}>
                      <SimpleDropdown
                        value={selectedYear}
                        onChange={handleYearChange}
                        options={yearOptions}
                        placeholder="Select Year"
                        variant="form"
                      />
                    </div>
                  </div>
                </div> */}

                {/* Show player table or empty state */}
                {teamRoster.length > 0 ? (
                  <PlayerTable
                    data={teamRoster}
                    loading={loading}
                    pagination={rosterPagination}
                    onLoadMore={handleLoadMore}
                    limit={rosterLimit}
                    onLimitChange={(val) => setRosterLimit(val)}
                    sortColumn={sortBy}
                    sortDirection={sortOrder}
                    onSort={handleSort}
                  />
                ) : !loading && (
                  <div className="text-center py-12 text-jp-muted">
                    <svg
                      className="w-16 h-16 mx-auto mb-4 text-jp-muted/70"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <p className="text-lg font-medium">No players found</p>
                    <p className="text-sm mt-2">No player data available for the {seasonLabel(selectedYear)} season.</p>
                    <p className="text-sm mt-1 text-jp-muted">Try selecting a different season year.</p>
                  </div>
                )}
              </section>
              )}

              {activeTab === 'statistics' && (
                <>
                  <section className={`${CARD} text-left`}>
                    <CardTitle title={`Player Stats · ${seasonLabel(selectedYear)}`} />
                    <RecentTeamStats
                      stats={currentTeamData._id ? currentTeamData : teamFromState}
                      showFilterModal={showFilterModal}
                      onFiltersClick={() => setShowFilterModal(!showFilterModal)}
                      onCloseModal={() => setShowFilterModal(false)}
                      onApplyFilters={handleApplyFilters}
                      statsType={scope}
                      onStatsTypeChange={setScope}
                      category={category}
                      onCategoryChange={setCategory}
                    />
                    <PlayerTable
                      data={filteredRoster}
                      loading={loading}
                      pagination={rosterPagination}
                      onLoadMore={handleLoadMore}
                      limit={rosterLimit}
                      onLimitChange={(val) => setRosterLimit(val)}
                      selectedStatColumns={statColumns}
                      filterActiveTab={statsTab}
                      sortColumn={sortBy}
                      sortDirection={sortOrder}
                      onSort={handleSort}
                    />
                  </section>
                  <TeamStatistics teamId={teamId} />
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoachTeamProfilePage;
